import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ModalBase from "./ModalBase";
import Select from "../ReusableComponents/Select";
import { setModalOpen } from "../../store/modalSlice";
import { noteForecastsUploaded } from "../../store/processedModelSchemaSlice";
import type { RootState } from "../../store";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import { TIME_ZONE_OPTIONS, isSupportedTimeZone, zonedDateTimeToUtcIso } from "../../utils/timeZoneOptions";
import { formatBytes } from "../../utils/datasetUploadApi";
import {
  ForecastModelLike,
  ForecastUploadLink,
  ForecastUploadStage,
  RunForecastUploadResult,
  describeForecastUpload,
  forecastOutputsOf,
  forecastUploadEventProperties,
  forecastUploadFileError,
  runForecastUpload,
  sentForecastUploadPlaceholder,
} from "../../utils/forecastUploadApi";
import { ReactComponent as UploadIcon } from "../../assets/icons/upload-02.svg";

// Upload a forecast into an external forecast model's output (docs/architecture/EXTERNAL_FORECASTS.md): the twin of
// the dataset upload mode of HistoricalDatasetModal. Opened from the model card or the model's detail modal with
// properties { schema, outputParamId? }. The file goes through forecastUploadApi (link → S3 → poll); the platform's
// file trigger checks it and files it under its issue time, where it appears in the model's forecasts.

const STAGE_LABELS: Record<ForecastUploadStage, string> = {
  requesting: "Requesting upload link",
  uploading: "Uploading file",
  processing: "Checking",
};
const STAGE_DETAILS: Record<ForecastUploadStage, string> = {
  requesting: "Checking access and preparing a secure upload link…",
  uploading: "Sending the file to storage…",
  processing: "The file arrived; it is being checked and filed under its issue time.",
};

/** A date and time in a file name (2026-10-28-12-00, 2026-10-28T12:00, 2026_10_28_1200, 20261028) → "YYYY-MM-DDTHH:mm" */
export const issueTimeFromFileName = (filename: string): string => {
  let match = filename.match(/(\d{4}[-_]\d{2}[-_]\d{2})[T_-](\d{2})[:-]?(\d{2})(?!\d)/);
  if (match) return `${match[1].replace(/_/g, "-")}T${match[2]}:${match[3]}`;
  match = filename.match(/(\d{4}[-_]\d{2}[-_]\d{2})[T_-](\d{2})(?!\d)/);
  if (match) return `${match[1].replace(/_/g, "-")}T${match[2]}:00`;
  match = filename.match(/(\d{4})[-_]?(\d{2})[-_]?(\d{2})(?!\d)/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}T00:00`;
  return "";
};

interface UploadForecastModalProps {
  onClose?: () => void;
}

const UploadForecastModal: React.FC<UploadForecastModalProps> = () => {
  const dispatch = useDispatch();
  const { capture } = usePostHogCapture();
  const modalState = useSelector((state: RootState) => state.modalState);
  const schema: ForecastModelLike | undefined = modalState?.properties?.schema;
  const outputs = useMemo(() => forecastOutputsOf(schema), [schema]);
  const [outputParamId, setOutputParamId] = useState<string>(
    () => modalState?.properties?.outputParamId || outputs[0]?.id || ""
  );

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [issueTime, setIssueTime] = useState<string>(""); // "YYYY-MM-DDTHH:mm" in issueTimeZone
  const autoIssueTime = useRef<string>("");
  const [issueTimeZone, setIssueTimeZone] = useState<string>(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("preferredIssueTimeZone") : null;
    return isSupportedTimeZone(stored) ? (stored as string) : "UTC";
  });
  const [uploadStage, setUploadStage] = useState<ForecastUploadStage | null>(null);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadResult, setUploadResult] = useState<RunForecastUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploadAbort = useRef<AbortController | null>(null);
  const uploadLink = useRef<ForecastUploadLink | null>(null);

  const isUploading = uploadStage !== null;
  const finalUpload = uploadResult?.upload ?? null;

  useEffect(() => {
    if (!outputParamId && outputs[0]?.id) setOutputParamId(outputs[0].id);
  }, [outputs, outputParamId]);

  useEffect(() => {
    try {
      window.localStorage.setItem("preferredIssueTimeZone", issueTimeZone);
    } catch {
      // storage unavailable: the preference just doesn't persist
    }
  }, [issueTimeZone]);

  const selectFile = (file: File | null | undefined) => {
    if (!file) return;
    const problem = forecastUploadFileError(file);
    if (problem) {
      setCsvFile(null);
      setError(problem);
      return;
    }
    setCsvFile(file);
    setUploadResult(null);
    setError(null);
    // The issue time follows the file name: a dated name replaces whatever the field held; an undated one clears a
    // value the previous file's name supplied and keeps one the user typed
    const extracted = issueTimeFromFileName(file.name);
    if (extracted) {
      setIssueTime(extracted);
      autoIssueTime.current = extracted;
    } else if (autoIssueTime.current && issueTime === autoIssueTime.current) {
      setIssueTime("");
      autoIssueTime.current = "";
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(e.target.files?.[0]);
    e.target.value = "";
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (isUploading) return;
    selectFile(e.dataTransfer?.files?.[0]);
  };

  const handleUpload = async () => {
    const problem = forecastUploadFileError(csvFile);
    if (problem || !csvFile) {
      setError(problem);
      return;
    }
    if (!outputParamId) {
      setError("This model has no forecast output to upload into.");
      return;
    }
    const controller = new AbortController();
    uploadAbort.current = controller;
    setError(null);
    setUploadResult(null);
    setUploadPercent(0);
    setUploadStage("requesting");
    uploadLink.current = null;
    const sentFile = csvFile;
    try {
      const result = await runForecastUpload(outputParamId, sentFile, {
        fileName: sentFile.name,
        issueTime: issueTime ? zonedDateTimeToUtcIso(issueTime, issueTimeZone) : null,
        signal: controller.signal,
        onStage: (stage) => setUploadStage(stage),
        onLinkIssued: (link) => {
          uploadLink.current = link;
        },
        onUploadProgress: (sent, total) => setUploadPercent(total ? Math.round((sent / total) * 100) : 0),
      });
      if (controller.signal.aborted) return;
      setUploadResult(result);
      setCsvFile(null); // the file is in S3 whatever the outcome; the next upload starts from a fresh pick
      if (schema) capture(POSTHOG_EVENTS.FORECAST_UPLOAD_COMPLETED, forecastUploadEventProperties(schema, outputParamId, sentFile, { result }));
      // The new issue is a forecast the cards and the output modal should show: tell the Models page to re-read
      if (result.upload.status === "LOADED") dispatch(noteForecastsUploaded());
    } catch (err: any) {
      if (controller.signal.aborted) return;
      setError(err?.message || "The upload failed.");
      if (schema) capture(POSTHOG_EVENTS.FORECAST_UPLOAD_COMPLETED, forecastUploadEventProperties(schema, outputParamId, sentFile, {}));
    } finally {
      if (uploadAbort.current === controller) uploadAbort.current = null;
      if (!controller.signal.aborted) setUploadStage(null);
    }
  };

  // Before the file is sent, cancel really cancels. Once S3 has it, the check runs regardless, so "stop waiting"
  // only stops the poll; the outcome shows in the model's Uploads tab.
  const handleCancelUpload = () => {
    const stage = uploadStage;
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    setUploadStage(null);
    setUploadPercent(0);
    if (stage === "processing" && csvFile) {
      setUploadResult({
        upload: sentForecastUploadPlaceholder(uploadLink.current ?? { uploadId: "", url: "", fields: {}, expiresAt: "", maxBytes: 0 }, csvFile, outputParamId),
        timedOut: true,
      });
      setCsvFile(null);
    }
  };

  const handleClose = () => {
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    dispatch(setModalOpen(false));
  };

  const handleUploadAnother = () => {
    setCsvFile(null);
    setUploadResult(null);
    setError(null);
  };

  const dateOf = issueTime ? issueTime.split("T")[0] : "";
  const [hourOf, minuteOf] = (issueTime ? issueTime.split("T")[1] || "00:00" : "00:00").split(":");
  const setIssueParts = (date: string, hour: string, minute: string) => setIssueTime(date ? `${date}T${hour}:${minute}` : "");

  const footer = (
    <>
      {finalUpload && !isUploading && (
        <button type="button" onClick={handleUploadAnother} className="eai-btn-outline" data-testid="upload-forecast-another">
          Upload another
        </button>
      )}
      <button type="button" onClick={isUploading ? handleCancelUpload : handleClose} className="eai-btn-outline">
        {isUploading ? (uploadStage === "processing" ? "Stop waiting" : "Cancel upload") : finalUpload ? "Close" : "Cancel"}
      </button>
      {!finalUpload && (
        <button
          type="button"
          onClick={handleUpload}
          disabled={isUploading || !csvFile || !outputParamId}
          className="eai-btn-cta disabled:pointer-events-none disabled:opacity-40"
          data-testid="upload-forecast-submit"
        >
          {isUploading ? (
            <>
              <svg className="-ml-1 mr-3 h-4 w-4 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {STAGE_LABELS[uploadStage as ForecastUploadStage] || "Working…"}
            </>
          ) : (
            <>
              <UploadIcon className="mr-2 h-4 w-4" />
              Upload forecast
            </>
          )}
        </button>
      )}
    </>
  );

  return (
    <ModalBase title={`Upload forecast — ${schema?.name || "model"}`} icon={<UploadIcon className="h-5 w-5" />} onClose={handleClose} size="lg" footer={footer}>
      <div className="space-y-4" data-testid="upload-forecast-modal">
        <p className="text-eai-body-sm text-eai-ink-subtle">
          One Standard time-series CSV per forecast issue: metadata rows (<code>forecast_issued</code>, <code>unit</code>…), a{" "}
          <code>DateTime (UTC)</code> header and one row per timestamp. The file is checked and filed under its issue
          time; it then appears in this model's forecasts, composed forecasts, verification and the API.
        </p>

        {outputs.length === 0 ? (
          <div className="rounded-md border border-eai-danger-border bg-eai-danger-surface p-3 text-eai-body-sm text-eai-danger-text">
            This model has no forecast output to upload into.
          </div>
        ) : outputs.length > 1 ? (
          <div>
            <label className="eai-meta-label mb-1 block">Output</label>
            <Select value={outputParamId} onChange={(e: any) => setOutputParamId(e.target.value)} containerClassName="w-full" disabled={isUploading}>
              {outputs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name || o.id}
                  {o.unit ? ` (${o.unit})` : ""}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <p className="text-eai-body-xs text-eai-ink-subtle">
            Output: <span className="font-medium text-eai-ink-muted">{outputs[0].name || outputs[0].id}</span>
            {outputs[0].unit ? ` (${outputs[0].unit})` : ""}
          </p>
        )}

        {isUploading && (
          <div className="p-4 bg-eai-surface-sunken border border-eai-border rounded-md" data-testid="upload-forecast-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="font-inter text-eai-body-sm font-medium text-eai-ink-muted">{STAGE_LABELS[uploadStage as ForecastUploadStage]}</span>
              {uploadStage === "uploading" && <span className="text-eai-body-sm text-eai-ink-subtle">{uploadPercent}%</span>}
            </div>
            <div className="w-full bg-eai-border rounded-full h-2 mb-2 overflow-hidden">
              <div
                className={`bg-eai-cta-primary h-2 rounded-full transition-all duration-300 ease-in-out ${uploadStage === "processing" ? "animate-pulse" : ""}`}
                style={{ width: uploadStage === "requesting" ? "5%" : uploadStage === "uploading" ? `${Math.max(5, uploadPercent)}%` : "100%" }}
              />
            </div>
            <p className="text-eai-body-xs text-eai-ink-subtle">{STAGE_DETAILS[uploadStage as ForecastUploadStage]}</p>
          </div>
        )}

        <div
          className={`rounded-eai-card border-2 border-dashed p-6 text-center ${isUploading ? "border-eai-border opacity-60" : "border-eai-border-hover"}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          data-testid="upload-forecast-dropzone"
        >
          <UploadIcon className="mx-auto h-12 w-12 text-eai-ink-faint" />
          <div className="mt-2">
            <label htmlFor="forecast-csv-upload" className={isUploading ? "cursor-default" : "cursor-pointer"}>
              <span className="text-eai-cta-primary hover:text-[eai-cta-primary-hover] font-medium">Click to upload</span>
              <span className="text-eai-ink-subtle"> or drag and drop</span>
              <input id="forecast-csv-upload" type="file" accept=".csv,text/csv" onChange={handleFileChange} disabled={isUploading} className="sr-only" />
            </label>
          </div>
          <p className="text-eai-body-xs text-eai-ink-subtle mt-1">.csv, up to 10 MB</p>
        </div>

        {csvFile && !finalUpload && (
          <div className="p-3 bg-eai-surface-sunken rounded-md">
            <p className="font-inter text-eai-body-sm font-medium text-eai-ink-muted">
              Selected: {csvFile.name} <span className="font-normal text-eai-ink-subtle">({formatBytes(csvFile.size)})</span>
            </p>
          </div>
        )}

        {csvFile && !finalUpload && (
          <div className="border-t border-eai-border pt-4">
            <label className="eai-meta-label mb-3 block">Forecast issue time</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-eai-body-xs text-eai-ink-subtle mb-1">Date &amp; time (24-hour)</label>
                <div className="flex items-center border border-eai-border-hover rounded-md focus-within:ring-1 focus-within:ring-eai-cta-primary overflow-hidden">
                  <input
                    type="date"
                    value={dateOf}
                    onChange={(e) => setIssueParts(e.target.value, hourOf, minuteOf)}
                    className="flex-1 px-3 py-2 border-0 focus:outline-none text-eai-body-sm"
                    data-testid="upload-forecast-issue-date"
                  />
                  <div className="flex items-center px-2 border-l border-eai-border-hover">
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={issueTime ? hourOf : ""}
                      onChange={(e) => dateOf && setIssueParts(dateOf, Math.min(23, Math.max(0, parseInt(e.target.value, 10) || 0)).toString().padStart(2, "0"), minuteOf)}
                      className="w-10 text-right border-0 bg-transparent focus:outline-none text-eai-body-sm"
                      placeholder="HH"
                    />
                    <span className="text-eai-ink-subtle mx-1">:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={issueTime ? minuteOf : ""}
                      onChange={(e) => dateOf && setIssueParts(dateOf, hourOf, Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)).toString().padStart(2, "0"))}
                      className="w-10 text-left border-0 bg-transparent focus:outline-none text-eai-body-sm"
                      placeholder="MM"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-eai-body-xs text-eai-ink-subtle mb-1">Issue time timezone</label>
                <Select value={issueTimeZone} onChange={(e: any) => setIssueTimeZone(e.target.value)} containerClassName="w-full">
                  {TIME_ZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <p className="mt-2 text-eai-body-xs text-eai-ink-subtle">
              {issueTime && issueTimeFromFileName(csvFile.name) === issueTime ? (
                <span className="text-eai-success-text">✓ Taken from the file name: {csvFile.name}</span>
              ) : (
                "When was this forecast issued? Leave it blank to use the file's forecast_issued row or the date in its name; a forecast with no issue time is rejected. Uploading the same issue again replaces it."
              )}
            </p>
          </div>
        )}

        {finalUpload && (
          <div
            className={`rounded-md border p-3 ${
              finalUpload.status === "LOADED"
                ? "border-eai-success-border bg-eai-success-surface-soft"
                : uploadResult?.timedOut
                  ? "border-eai-border bg-eai-surface-sunken"
                  : "border-eai-danger-border bg-eai-danger-surface"
            }`}
            data-testid="upload-forecast-result"
          >
            <p
              className={`text-eai-body-sm font-medium ${
                finalUpload.status === "LOADED" ? "text-eai-success-text-strong" : uploadResult?.timedOut ? "text-eai-ink-muted" : "text-eai-danger-text"
              }`}
            >
              {uploadResult?.timedOut
                ? "Still checking"
                : finalUpload.status === "LOADED"
                  ? "Forecast loaded"
                  : finalUpload.status === "REJECTED"
                    ? "File rejected"
                    : finalUpload.status === "FAILED"
                      ? "Processing failed"
                      : "Link expired"}
              {" — "}
              {finalUpload.fileName}
            </p>
            {uploadResult?.timedOut ? (
              <p className="mt-1 text-eai-body-xs text-eai-ink-subtle">
                The file was received and is still being checked. You can close this window; the outcome will show in the
                model's Uploads tab.
                {uploadResult.readError ? ` (Its status could not be read just now: ${uploadResult.readError})` : ""}
              </p>
            ) : finalUpload.status === "REJECTED" && finalUpload.validationErrors?.length ? (
              <ul className="mt-1 list-disc pl-5 text-eai-body-xs text-eai-danger-text">
                {finalUpload.validationErrors.map((problem, i) => (
                  <li key={i}>{problem}</li>
                ))}
              </ul>
            ) : (
              <p className={`mt-1 text-eai-body-xs ${finalUpload.status === "LOADED" ? "text-eai-success-text" : "text-eai-danger-text"}`}>
                {describeForecastUpload(finalUpload)}
              </p>
            )}
            {finalUpload.status === "LOADED" && (finalUpload.warnings?.length || 0) > 0 && (
              <ul className="mt-1 list-disc pl-5 text-eai-body-xs text-eai-warning-text">
                {finalUpload.warnings!.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            )}
            {finalUpload.status === "FAILED" && (
              <p className="mt-1 text-eai-body-xs text-eai-ink-subtle">
                This is a platform error, not a problem with the file; it has been logged for the Erode team.
              </p>
            )}
          </div>
        )}

        {error && <p className="text-eai-body-sm text-eai-danger-text">{error}</p>}
      </div>
    </ModalBase>
  );
};

export default UploadForecastModal;
